import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Lock, User, Loader2, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../lib/AuthContext';

const AdminLogin = () => {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#0B2545] text-white">
        Loading...
      </div>
    );
  }
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(form.username, form.password);
      navigate('/admin', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#0B2545] via-[#0B2545] to-[#11305c] p-6">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 30%, #C9A86A 0%, transparent 35%), radial-gradient(circle at 80% 70%, #C9A86A 0%, transparent 35%)'
        }}
      />
      <div className="relative w-full max-w-md">
        <div className="mb-6 text-center text-white">
          <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#C9A86A] text-white">
            <ShieldCheck size={22} />
          </div>
          <h1 className="font-serif text-2xl font-bold">State House Admin</h1>
          <p className="mt-1 text-[13px] text-white/70">
            Sign in to manage website content
          </p>
        </div>
        <form
          onSubmit={submit}
          className="rounded-xl border border-white/10 bg-white/[0.04] p-7 backdrop-blur-md shadow-2xl"
        >
          <div className="mb-4">
            <label className="mb-1.5 block text-[12.5px] font-medium text-white/85">
              Username
            </label>
            <div className="flex items-center gap-2 rounded-md border border-white/20 bg-white/10 px-3 focus-within:border-[#C9A86A]">
              <User size={15} className="text-white/60" />
              <input
                required
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                className="w-full bg-transparent py-2.5 text-[14px] text-white outline-none placeholder:text-white/40"
                placeholder="admin"
                autoFocus
              />
            </div>
          </div>
          <div className="mb-5">
            <label className="mb-1.5 block text-[12.5px] font-medium text-white/85">
              Password
            </label>
            <div className="flex items-center gap-2 rounded-md border border-white/20 bg-white/10 px-3 focus-within:border-[#C9A86A]">
              <Lock size={15} className="text-white/60" />
              <input
                required
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full bg-transparent py-2.5 text-[14px] text-white outline-none placeholder:text-white/40"
                placeholder="••••••••••••••••"
              />
            </div>
          </div>
          {error && (
            <div className="mb-4 rounded-md border border-red-400/30 bg-red-500/10 px-3 py-2 text-[12.5px] text-red-200">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-[#C9A86A] py-2.5 text-[14px] font-medium text-white transition hover:bg-[#b8975c] disabled:opacity-60"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Sign In
          </button>
          <p className="mt-4 text-center text-[11.5px] text-white/50">
            Protected area. Authorised personnel only.
          </p>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
